import {Component, inject, OnDestroy, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {NgClass, NgOptimizedImage} from "@angular/common";
import {isActive, Router, RouterLink} from "@angular/router";
import {CdkMenu, CdkMenuBar, CdkMenuItem, CdkMenuTrigger} from "@angular/cdk/menu";
import {AuthService} from "@app/_services/auth.service";
import {Subscription} from "rxjs";
import {LiveboardMenuItems} from "@app/_models/thoughtspot";
import {ThoughtspotService} from "@app/_services/thoughtspot.service";
import {NgbCollapse} from "@ng-bootstrap/ng-bootstrap";
import {environment} from "@environments/environment";
import {IdentityUser} from "@app/_models/identityUser";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {FeatureFlagService} from "@app/_services/feature-flag.service";
import {Feature} from "@app/_models/api";

interface NavItem {
    label: string;
    icon?: string;
    link: string;
    queryParams?: Record<string, string>;
    dividerBefore?: boolean;
}

interface NavSection {
    label: string;
    icon: string;
    items: NavItem[];
}

@Component({
    selector: 'app-header',
    imports: [
        NgOptimizedImage,
        RouterLink,
        CdkMenuBar,
        CdkMenu,
        CdkMenuItem,
        CdkMenuTrigger,
        NgClass,
        NgbCollapse
    ],
    templateUrl: './header.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./header.component.scss', '../app.component.scss']
})
export class HeaderComponent implements OnDestroy {
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;
    private userSubscription: Subscription;
    private enabledFeaturesSubscription: Subscription;
    protected readonly environment = environment;

    liveboardsSubscription: Subscription;
    liveboardsDisplay: LiveboardMenuItems = new LiveboardMenuItems();
    enabledFeatures = inject(FeatureFlagService).enabledFeatures();
    thoughtSpotFullEmbeds: typeof environment.thoughtSpotConfig.fullEmbeds = [];
    isMenuCollapsed: boolean;

    constructor(public authService: AuthService, private thoughtspotService: ThoughtspotService, private router: Router) {
        this.isMenuCollapsed = true;
        this.userSubscription = this.authService.user().subscribe((user: IdentityUser | null) => user);

        this.liveboardsSubscription = this.thoughtspotService.liveboards().subscribe({
            next: (liveboardMenuItems: LiveboardMenuItems) => this.liveboardsDisplay = liveboardMenuItems,
        });

        this.enabledFeaturesSubscription = this.enabledFeatures.subscribe({
            next: (features: Feature[] | null) => {
                if (features != null) {
                    this.thoughtSpotFullEmbeds = environment.thoughtSpotConfig.fullEmbeds.filter((element) => {
                        return element.featureFlag == null || features.some((feature: Feature) => {
                            return feature.code == element.featureFlag
                        });
                    });
                }
            },
        });
    }

    // CDK menu panels are only in the DOM while open, so routerLinkActive cannot see their links.
    // Ask the Router directly instead.
    isActive(link: string, queryParams?: Record<string, string>): boolean {
        return isActive(this.router.createUrlTree([link], {queryParams}), this.router,
            {paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored'})();
    }

    isSectionActive(section: NavSection): boolean {
        return section.items.some((item) => this.isActive(item.link, item.queryParams));
    }

    // Rebuilt and checked on every change detection: about 3 microseconds per link, so it
    // stays under 1ms until the menu holds several hundred links. Memoize before it gets there.
    sections(): NavSection[] {
        const liveboards: NavItem[] = this.liveboardsDisplay.admin.map((lb, i) =>
            ({label: lb.name, link: '/analytics/liveboard', queryParams: {id: lb.id}, dividerBefore: i === 0}));
        return [
            {label: 'Analytics', icon: 'bi-graph-up', items: [
                ...this.thoughtSpotFullEmbeds.map((e) => ({label: e.name, icon: e.icon, link: e.routerLink})),
                ...liveboards,
            ]},
            {label: 'Monitoring', icon: 'bi-display', items: [
                {label: '852 File Summary', icon: 'bi-file-earmark', link: '/processing/edi-852-file-summary'},
                {label: '867 File Summary', icon: 'bi-file-earmark', link: '/processing/edi-867-file-summary'},
                {label: 'Request History', icon: 'bi-clock-history', link: '/processing/requests'},
                {label: 'Extracts', icon: 'bi-file-text', link: '/extracts'},
            ]},
            {label: 'MDM', icon: 'bi-database', items: [
                {label: 'Trading Partners', icon: 'bi-truck', link: '/master-data/trading-partners'},
                {label: 'Distribution Centers', icon: 'bi-buildings', link: '/master-data/distribution-centers'},
                {label: 'Product Groups', icon: 'bi-box-seam', link: '/master-data/product-groups'},
                {label: 'Master Data Loads', icon: 'bi-cloud-upload', link: '/master-data/master-data-loads'},
                {label: '852 ZA Code Maps', icon: 'bi-diagram-3', link: '/mappings'},
                {label: 'EDI Sender/Receivers', icon: 'bi-shield-check', link: '/master-data/edi-sender-receivers'},
                {label: '997 Config', icon: 'bi-file-code', link: '/processing/edi-997-config'},
                {label: 'Extract Config', icon: 'bi-file-earmark-spreadsheet', link: '/processing/extract-config'},
            ]},
            {label: 'Datasources', icon: 'bi-file-earmark-arrow-up', items: [
                {label: 'Web Data Connections', icon: 'bi-globe', link: '/web-data-connections'},
            ]},
            {label: 'Admin', icon: 'bi-gear', items: [
                {label: 'Manage Admin Users', icon: 'bi-person-circle', link: '/admin-users'},
                {label: 'Manage Users', icon: 'bi-person', link: '/users'},
                {label: 'Announcements', icon: 'bi-megaphone', link: '/announcements'},
                {label: 'Audit Log', icon: 'bi-journal-text', link: '/audit-log'},
                {label: 'Feature Flags', icon: 'bi-flag', link: '/feature-flags'},
            ]},
        ];
    }

    ngOnDestroy(): void {
        this.userSubscription.unsubscribe();
        this.liveboardsSubscription.unsubscribe();
        this.enabledFeaturesSubscription.unsubscribe();
    }

    onLogout() {
        this.authService.signout().subscribe(() => {
            this.authService.logout();
        });
    }
}
